import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, User, Calendar, MessageSquare, Send } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import CommentThread from '@/components/CommentThread';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ThreadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [thread, setThread] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [threadRes, commentsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/community/threads/${id}`),
        axios.get(`${BACKEND_URL}/api/community/threads/${id}/comments`)
      ]);
      setThread(threadRes.data);
      setComments(commentsRes.data);
    } catch (error) {
      console.error('Error loading thread:', error);
    } finally {
      setLoading(false);
    }
  };

  const postComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || submitting) return;

    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/community/threads/${id}/comments`, {
        thread_id: id,
        content: newComment,
        parent_comment_id: null
      }, { withCredentials: true });
      setNewComment('');
      loadData();
    } catch (error) {
      console.error('Error posting comment:', error);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center pt-20"><div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
  }

  if (!thread) {
    return <div className="min-h-screen flex items-center justify-center pt-20"><p>Thread not found</p></div>;
  }

  return (
    <div className="min-h-screen pt-20" data-testid="thread-detail-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <button onClick={() => navigate('/community')} className="flex items-center space-x-2 text-muted-foreground hover:text-foreground transition-colors mb-8"><ArrowLeft className="w-5 h-5" /><span>Back to Community</span></button>

        <div className="bg-card border border-border rounded-lg p-8 mb-8">
          <div className="flex items-center space-x-4 mb-4">
            <img src={thread.user_picture} alt={thread.user_name} className="w-12 h-12 rounded-full border-2 border-primary" />
            <div>
              <div className="flex items-center space-x-3">
                <span className="font-medium">{thread.user_name}</span>
                <span className="text-xs px-3 py-1 bg-primary/20 text-primary rounded-full">{thread.category}</span>
              </div>
              <div className="flex items-center space-x-1 text-sm text-muted-foreground mt-1"><Calendar className="w-4 h-4" /><span>{formatDistanceToNow(new Date(thread.created_at), { addSuffix: true })}</span></div>
            </div>
          </div>
          <h1 className="font-heading text-3xl font-bold mb-4">{thread.title}</h1>
          {thread.content && <p className="text-foreground leading-relaxed whitespace-pre-wrap">{thread.content}</p>}
        </div>

        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-6"><MessageSquare className="w-6 h-6 text-primary" /><h2 className="font-heading text-2xl font-bold">Discussion</h2><span className="text-muted-foreground">({comments.length})</span></div>

          <form onSubmit={postComment} className="mb-8" data-testid="new-comment-form">
            <div className="flex space-x-3">
              <input type="text" value={newComment} onChange={(e) => setNewComment(e.target.value)} placeholder="Join the discussion..." className="flex-1 px-6 py-3 bg-card border border-border rounded-full text-base focus:outline-none focus:ring-2 focus:ring-primary" data-testid="comment-input" />
              <button type="submit" disabled={submitting || !newComment.trim()} className="px-6 py-3 bg-primary text-primary-foreground rounded-full font-semibold btn-glow hover:opacity-90 transition-all disabled:opacity-50 flex items-center space-x-2" data-testid="comment-submit-button"><Send className="w-5 h-5" /><span className="hidden sm:inline">Post</span></button>
            </div>
          </form>

          {comments.length > 0 ? <CommentThread comments={comments} movieId={id} onCommentAdded={loadData} /> : <p className="text-center text-muted-foreground py-12">No comments yet. Be the first to share your thoughts!</p>}
        </div>
      </div>
    </div>
  );
}